import { useEffect, useRef, useState } from "react";
import { X, CreditCard, Loader2, AlertTriangle, Smartphone, ChevronLeft } from "lucide-react";
import api, { formatApiError, resolveImg } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { trackInitiateCheckout } from "@/lib/fbPixel";

const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 40; // ~2 minutes

export default function PurchaseModal({ track, onClose }) {
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const [providers, setProviders] = useState(null); // null = loading, [] = none available
  // A provider can now appear more than once (one entry per currency it can
  // charge in — see registry.list_available_payment_providers), so the
  // selection key is `${id}::${charge_currency}`, not just the provider id.
  // The fan picks in two steps instead of facing every (provider, currency)
  // combination as one long flat list: first the provider itself, then —
  // only if that provider offers more than one currency — which currency.
  const [selectedProviderId, setSelectedProviderId] = useState(null);
  const [selectedKey, setSelectedKey] = useState(null);
  const [msisdn, setMsisdn] = useState("");
  const [method, setMethod] = useState("");
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false);
  const pollTimer = useRef(null);

  const offerKey = (p) => `${p.id}::${p.charge_currency}`;

  useEffect(() => {
    if (!track) return;
    setProviders(null);
    setSelectedProviderId(null);
    setSelectedKey(null);
    setMsisdn("");
    setMethod("");
    setAwaitingConfirmation(false);
    (async () => {
      try {
        const { data } = await api.get("/payments/providers", {
          params: { currency: track.currency },
        });
        setProviders(data?.providers || []);
      } catch {
        setProviders([]); // fail safe: no method rather than a broken button
      }
    })();
    return () => clearTimeout(pollTimer.current);
  }, [track]);

  if (!track) return null;

  // One entry per provider, in the order the backend already returns them —
  // this is what step 1 shows, regardless of how many currencies each one
  // can charge in.
  const providerGroups = [];
  const seenProviderIds = new Set();
  for (const p of providers || []) {
    if (seenProviderIds.has(p.id)) continue;
    seenProviderIds.add(p.id);
    providerGroups.push(p);
  }
  const offersForSelectedProvider = selectedProviderId
    ? (providers || []).filter((p) => p.id === selectedProviderId)
    : [];
  // More than one currency doesn't get its own screen to scroll through —
  // it's a dropdown next to the converted amount, defaulted to a sensible
  // choice, on the same screen as the phone/operator fields.
  const hasMultipleCurrencies = offersForSelectedProvider.length > 1;
  const step = selectedProviderId ? "details" : "provider";

  const pickProvider = (providerId) => {
    setSelectedProviderId(providerId);
    const offers = (providers || []).filter((p) => p.id === providerId);
    // Default to the track's own currency (no conversion) when the
    // provider takes it directly; otherwise XAF (Gabon, the confirmed
    // primary market) if the provider offers it, falling back to whichever
    // comes first (alphabetical, from the backend) rather than leaving it
    // unpicked.
    const preferred =
      offers.find((o) => !o.converted) ||
      offers.find((o) => o.charge_currency === "XAF") ||
      offers[0] ||
      null;
    setSelectedKey(preferred ? offerKey(preferred) : null);
  };

  const selected = providers?.find((p) => offerKey(p) === selectedKey) || null;
  const requiresPhone = !!selected?.requires_phone;
  const knownOperators = selected?.operators || [];

  // Providers with a confirmed operator list (registry._PROVIDER_OPERATORS)
  // get a real selector instead of a free-text field the fan has to type
  // into — pick a sensible default (first choice) whenever the currently
  // selected offer changes, mirroring how the offer itself defaults to the
  // first entry above.
  useEffect(() => {
    if (knownOperators.length && !knownOperators.some((op) => op.code === method)) {
      setMethod(knownOperators[0].code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey]);

  // Direct-charge providers (PawaPay-style) push a payment request to the
  // fan's phone instead of hosting a checkout page — no URL to redirect to,
  // so confirmation is polled from the existing checkout/status endpoint
  // (payments/service.py's get_transaction_status, already built as a
  // polling fallback for every provider) instead of relying on a redirect.
  const pollStatus = async (sessionId, attempt = 0) => {
    if (attempt >= POLL_MAX_ATTEMPTS) {
      setAwaitingConfirmation(false);
      toast.error(t.purchase.timeout || "Délai dépassé — réessaie si le paiement n'a pas abouti.");
      return;
    }
    try {
      const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
      if (data?.payment_status === "paid") {
        window.location.href = `/payment/success?session_id=${sessionId}`;
        return;
      }
      if (data?.status === "expired" || data?.status === "FAILED" || data?.status === "CANCELLED") {
        setAwaitingConfirmation(false);
        toast.error(t.purchase.failed || "Le paiement a échoué.");
        return;
      }
    } catch {
      // transient error — keep polling, the fan's phone confirmation isn't affected
    }
    pollTimer.current = setTimeout(() => pollStatus(sessionId, attempt + 1), POLL_INTERVAL_MS);
  };

  const submit = async () => {
    if (!selected) return;
    if (requiresPhone && (!msisdn.trim() || !method.trim())) return;
    setLoading(true);
    try {
      // The chosen provider (whichever is enabled and supports this track's
      // currency) hosts its own checkout page where the fan picks Mobile
      // Money, card, etc. — no "purchase confirmed" until the provider's
      // webhook/status says so. Direct-charge providers (requiresPhone) skip
      // the redirect entirely and go straight to polling below. charge_currency
      // disambiguates which of this provider's offered currencies was picked
      // (see registry.list_available_payment_providers — a provider can now
      // appear more than once, once per chargeable currency).
      const { data } = await api.post("/payments/checkout/session", {
        // a whole project (album / EP / maxi) is bought in one payment: same checkout, release_id instead of track_id
        ...(track.kind === "release" ? { release_id: track.id } : { track_id: track.id }),
        origin_url: window.location.origin,
        provider: selected.id,
        charge_currency: selected.charge_currency,
        ...(requiresPhone ? { msisdn: msisdn.trim(), method: method.trim() } : {}),
      });
      trackInitiateCheckout(track.price, track.kind);
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      if (requiresPhone && data?.session_id) {
        setAwaitingConfirmation(true);
        pollStatus(data.session_id);
        return;
      }
      toast.error(t.purchase.error || "Erreur");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    setSelectedProviderId(null);
    setSelectedKey(null);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-md grid place-items-center p-4" data-testid="purchase-modal">
      <div className="relative w-full max-w-md max-h-[85dvh] bg-zinc-950 border border-white/10 rounded-3xl anim-in flex flex-col">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-white transition z-10"
          data-testid="purchase-close"
          aria-label="close"
        >
          <X className="w-4 h-4" />
        </button>

        {awaitingConfirmation ? (
          <div className="p-8 flex flex-col items-center text-center gap-3" data-testid="purchase-awaiting-confirmation">
            <Loader2 className="w-8 h-8 text-[#00F2FE] animate-spin" />
            <p className="text-white font-semibold">
              {t.purchase.checkPhone || "Vérifie ton téléphone pour confirmer le paiement"}
            </p>
            <p className="text-xs text-zinc-500">
              {t.purchase.checkPhoneHint || "Une demande de paiement Mobile Money vient de t'être envoyée."}
            </p>
          </div>
        ) : (
        <div className="p-6 pb-4 overflow-y-auto">
          {step !== "provider" && (
            <button
              type="button"
              onClick={goBack}
              className="mb-3 inline-flex items-center gap-1 text-xs font-semibold text-zinc-400 hover:text-white transition"
              data-testid="purchase-back-btn"
            >
              <ChevronLeft className="w-4 h-4" /> {t.purchase.back || "Retour"}
            </button>
          )}

          <h2 className="text-2xl font-black font-display text-white">{t.purchase.title}</h2>

          <div className="mt-5 flex items-center gap-4 bg-zinc-900/70 rounded-2xl p-3 border border-white/5">
            <img src={resolveImg(track.cover)} alt="" className="w-16 h-16 rounded-xl object-cover" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-zinc-500 uppercase tracking-wider">{track.kind === "release" ? "Projet complet" : t.purchase.track}</p>
              <p className="text-white font-semibold truncate">{track.title}</p>
              <p className="text-xs text-zinc-400 truncate">{track.artist_name}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-zinc-500 uppercase tracking-wider">{t.purchase.price}</p>
              <p className="text-xl font-black font-display text-white">${track.price.toFixed(2)}</p>
            </div>
          </div>

          <p className="mt-6 text-xs uppercase tracking-wider text-zinc-500 font-bold">
            {step === "provider" ? (t.purchase.method || "Moyen de paiement") : (t.purchase.detailsStep || "Finaliser")}
          </p>

          {providers === null && (
            <div className="mt-3 flex items-center gap-2 p-3 rounded-2xl border border-white/10 bg-zinc-900/60 text-zinc-400 text-xs">
              <Loader2 className="w-4 h-4 animate-spin shrink-0" />
              {t.purchase.loadingMethods || "Chargement des moyens de paiement…"}
            </div>
          )}

          {providers !== null && providers.length === 0 && (
            <div className="mt-3 flex items-center gap-2 p-3 rounded-2xl border border-amber-500/30 bg-amber-500/10 text-amber-200 text-xs" data-testid="no-payment-methods">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              {t.purchase.noMethods || `Aucun moyen de paiement disponible pour ${track.currency || "cette devise"} en ce moment.`}
            </div>
          )}

          {/* Step 1: pick a provider — one button each, never one per currency. */}
          {step === "provider" && providers !== null && providers.length > 0 && (
            <div className="mt-3 space-y-2">
              {providerGroups.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => pickProvider(p.id)}
                  data-testid={`pay-provider-${p.id}`}
                  className="w-full flex items-center gap-3 p-3 rounded-2xl border border-white/10 bg-zinc-900/60 hover:border-white/20 transition text-left"
                >
                  <span className="w-10 h-10 grid place-items-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 shrink-0">
                    <CreditCard className="w-5 h-5 text-white" />
                  </span>
                  <span className="block text-white font-semibold text-sm flex-1">{p.label}</span>
                </button>
              ))}
            </div>
          )}

          {/* Step 2: the chosen provider, a currency dropdown right next to
              the converted amount when it offers more than one (no separate
              screen to scroll through), then phone/operator if needed. */}
          {step === "details" && selected && (
            <div className="mt-3 space-y-2">
              <div className="flex items-center gap-3 p-3 rounded-2xl border border-[#00F2FE]/30 bg-[#00F2FE]/5">
                <span className="w-10 h-10 grid place-items-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 shrink-0">
                  <CreditCard className="w-5 h-5 text-white" />
                </span>
                <span className="text-white font-semibold text-sm flex-1 min-w-0 truncate">{selected.label}</span>
                {selected.charge_currency && (
                  <div className="text-right shrink-0">
                    {hasMultipleCurrencies ? (
                      <select
                        value={selectedKey}
                        onChange={(e) => setSelectedKey(e.target.value)}
                        data-testid="purchase-currency-select"
                        className="bg-white/10 border border-white/10 rounded-lg px-2 py-1 text-white text-sm font-semibold outline-none focus:border-[#00F2FE]/40"
                      >
                        {offersForSelectedProvider.map((p) => (
                          <option key={offerKey(p)} value={offerKey(p)} className="bg-zinc-900">
                            {p.charge_currency}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-zinc-400 text-sm font-semibold">{selected.charge_currency}</span>
                    )}
                    {/* Shown for ANY price once converted — not just when
                        there's a currency to pick between — so a fan always
                        sees the real amount they'll be charged, not just the
                        USD listing price. */}
                    {selected.converted && (
                      <p className="text-[11px] text-[#00F2FE]/90 mt-1 whitespace-nowrap" data-testid="purchase-converted-amount">
                        ≈ {(track.price * selected.rate).toLocaleString(undefined, { maximumFractionDigits: 0 })} {selected.charge_currency}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {requiresPhone && (
                <>
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-wider text-zinc-400 font-bold flex items-center gap-1.5">
                      <Smartphone className="w-3.5 h-3.5" /> {t.purchase.phoneLabel || "Numéro Mobile Money"}
                    </span>
                    <input
                      value={msisdn}
                      onChange={(e) => setMsisdn(e.target.value)}
                      placeholder="+24177000000"
                      className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white outline-none focus:border-[#00F2FE]/40 text-sm"
                      data-testid="purchase-msisdn-input"
                    />
                  </label>
                  <div className="block">
                    <span className="text-[11px] uppercase tracking-wider text-zinc-400 font-bold">
                      {t.purchase.operatorLabel || "Opérateur"}
                    </span>
                    {knownOperators.length > 0 ? (
                      <div className="mt-1 grid grid-cols-2 gap-2">
                        {knownOperators.map((op) => (
                          <button
                            key={op.code}
                            type="button"
                            onClick={() => setMethod(op.code)}
                            data-testid={`purchase-operator-${op.code}`}
                            className={`px-3 py-2.5 rounded-xl border text-sm font-semibold transition ${
                              method === op.code
                                ? "border-[#00F2FE] bg-[#00F2FE]/10 text-white"
                                : "border-white/10 bg-white/5 text-zinc-300 hover:border-white/20"
                            }`}
                          >
                            {op.label}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <input
                        value={method}
                        onChange={(e) => setMethod(e.target.value)}
                        placeholder="ex. AIRTEL_GAB"
                        className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white outline-none focus:border-[#00F2FE]/40 text-sm"
                        data-testid="purchase-operator-input"
                      />
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        )}

        {!awaitingConfirmation && (
        <div className="p-4 border-t border-white/5 flex items-center gap-2 bg-black/40 shrink-0">
          <button onClick={onClose} className="btn-secondary flex-1" data-testid="pay-cancel-btn">
            {t.purchase.cancel}
          </button>
          <button
            onClick={submit}
            disabled={loading || !selected || (requiresPhone && (!msisdn.trim() || !method.trim()))}
            className="btn-primary flex-1 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
            data-testid="pay-confirm-btn"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {t.purchase.pay}
          </button>
        </div>
        )}
      </div>
    </div>
  );
}
